import React from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const queryClient = new QueryClient();

const fetchLabStats = async () => {
    const response = await fetch(process.env.REACT_APP_URL_GET_LAB_STATS, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error('Errore nel caricamento delle statistiche');
    }
    return response.json();
};

function LabStatsChart() {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['lab-stats-observed-users'],
        queryFn: fetchLabStats,
        refetchOnWindowFocus: false,
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress sx={{ color: '#a71e3b' }} />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ mt: 4 }}>
                <Alert severity="error">
                    Errore nel caricamento delle statistiche: {error?.message}
                </Alert>
            </Box>
        );
    }

    const labs = data?.data || [];
    const topLabs = labs.slice(0, 20);

    const chartData = {
        labels: topLabs.map(lab => `${lab.labName} (${lab.idLab})`),
        datasets: [
            {
                label: 'Utenti osservati',
                data: topLabs.map(lab => lab.observedUsers),
                backgroundColor: '#a71e3b',
            },
            {
                label: 'Studenti iscritti',
                data: topLabs.map(lab => lab.totalStudents),
                backgroundColor: '#666',
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    font: { size: 11 },
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Numero utenti',
                },
                beginAtZero: true,
            },
        },
    };

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Laboratori per utenti osservati vs iscritti (Top 20)
            </Typography>

            {topLabs.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                    Nessun dato disponibile
                </Typography>
            ) : (
                <Bar data={chartData} options={options} height={120} />
            )}
        </Paper>
    );
}

const fetchTopLabsPoweredOn = async () => {
    // Step 1: Avvia la query
    const startResponse = await fetch(process.env.REACT_APP_URL_START_TOP_LABS_POWERED_ON, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    
    if (!startResponse.ok) {
        throw new Error(`Errore nell'avvio della query (${startResponse.status})`);
    }
    
    const { operationId } = await startResponse.json();
    console.log(`[TopLabsPoweredOn] Query avviata, operationId: ${operationId}`);
    
    // Step 2: Polling per il risultato (max 120s, ogni 3s)
    const maxAttempts = 40;
    const pollInterval = 3000;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
        const pollResponse = await fetch(
            `${process.env.REACT_APP_URL_POLL_TOP_LABS_POWERED_ON}/${operationId}`,
            { method: 'GET', credentials: 'include' }
        );
        
        if (pollResponse.status === 202) {
            console.log(`[TopLabsPoweredOn] Polling attempt ${attempt + 1}/${maxAttempts}: pending...`);
            continue;
        }
        
        if (!pollResponse.ok) {
            throw new Error(`Errore nel polling (${pollResponse.status})`);
        }
        
        const result = await pollResponse.json();
        if (result.status === 'complete') {
            console.log(`[TopLabsPoweredOn] Risultato ricevuto dopo ${(attempt + 1) * 3}s`);
            return result;
        }
    }
    
    throw new Error('Timeout: la query Horizon ha impiegato troppo tempo');
};

function TopLabsByPoweredOnChart() {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['top-labs-powered-on'],
        queryFn: fetchTopLabsPoweredOn,
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: 3000,
        staleTime: 30000,
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress sx={{ color: '#1e88e5' }} />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ mt: 4 }}>
                <Alert severity="error">
                    Errore nel caricamento delle statistiche VM: {error?.message}
                </Alert>
            </Box>
        );
    }

    const labs = data?.result || data?.data || [];

    // Estrai l'idLab corretto dall'inizio del poolName (es. "2937 Laboratorio..." → 2937)
    const extractIdFromPoolName = (poolName) => {
        const match = poolName?.match(/^(\d+)/);
        return match ? match[1] : null;
    };

    const chartData = {
        labels: labs.map(lab => {
            const name = lab.poolName || lab.labName || '';
            const id = extractIdFromPoolName(name) || lab.idLab || '';
            return `${name} (${id})`;
        }),
        datasets: [
            {
                label: 'VM accese',
                data: labs.map(lab => lab.poweredOnCount),
                backgroundColor: '#1e88e5',
            },
        ],
    };

    const options = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: { position: 'top' },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Numero VM accese',
                },
                beginAtZero: true,
            },
            y: {
                ticks: {
                    font: { size: 11 },
                },
            },
        },
    };

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Laboratori per VM accese (Top 20 - live da Horizon)
            </Typography>

            {labs.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                    Nessun dato disponibile
                </Typography>
            ) : (
                <Bar data={chartData} options={options} />
            )}
        </Paper>
    );
}

function LabStatsPage() {
    return (
        <Box sx={{ mt: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, color: '#a71e3b', fontWeight: 600 }}>
                Statistiche Laboratori
            </Typography>

            <QueryClientProvider client={queryClient}>
                <LabStatsChart />
                <Box sx={{ mt: 4 }}>
                    <TopLabsByPoweredOnChart />
                </Box>
            </QueryClientProvider>
        </Box>
    );
}

export default LabStatsPage;
